---
title: EcosystemCode
slug: ecosystemcode
type: work
status: published
featured: true
summary: Words become validated UML and a governed ontology, then a deterministic full-stack baseline. Architecture should not evaporate between the diagram and the code.
sitePublishedAt: "2026-09-18"
lastReviewedAt: "2026-10-01"
audiences:
  - product-developer
  - business
themes:
  - platform-architecture
  - product-lines
sourceUrl: https://ecosystemcode.com
originalAuthor: Jay van Zyl
sourceRelationship: founder-architect narrative of first-party product work
rightsStatus: first-party
seoDescription: EcosystemCode turns requirements into validated UML and OWL/RDF, then generates a deterministic React, API, MongoDB, Docker, and MCP baseline.
canonicalPath: /work/ecosystemcode
---

EcosystemCode is the product-line idea in software form. Start with a working system — UI, API, database — deterministic and reproducible. Add AI only where you turn it on. Architecture intent should not evaporate between a requirements doc, a diagram, and a backlog.

The application is the modeling workbench. Its public face is [ecosystemcode.com](https://ecosystemcode.com). This page is the personal case: why the generator exists, and what “separation of concerns” means when the output has to run. The modeling app itself is private. The ontology it consumes is public.

## Flow

1. **Words → UML** — class, sequence, state, activity, package, and component diagrams from epics, stories, or plain language.
2. **Check before generating** — missing entities, undefined states, incomplete workflows, duplicate names, orphaned relationships.
3. **Generate a runnable baseline** — default path is deterministic templates. Optional AI-enhanced mode when you want richer, variable output.
4. **Collaborate** — projects, sharing, stakeholder review.

Generation produces a React UI, Node API with a repository pattern, MongoDB schemas, auth derived from ontology roles, OpenAPI, an MCP server for CRUD, Docker, and synthetic data aligned to the class model. Traceability claim: **1:1 diagram → code → ontology**.

## Ontology

A living OWL/RDF/Turtle model sits beside the UML. Roles in the ontology become suggested, then enforced, authorization. The public seed set is [ecosystem-domain-catalog](https://github.com/ecogenetic/ecosystem-domain-catalog); the live catalog is [ontology.ecosystem.ai](https://ontology.ecosystem.ai).

This is the same separation-of-concerns argument I have been writing since the early product-line papers: keep commonality in a platform core, bind variability at the edges, and do not let a physical schema become the canonical language.

## Who it is for

Product teams who need a running prototype, architects who need a domain/API spike, analysts who want requirements as code, consultants who should hand over a baseline rather than slides.
